<script setup lang="ts">
import moment from 'moment'
import { computed as vueComputed, ref as vueRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import CustomDataTable from '@/components/CustomDataTable.vue'
import { useApiFirebase } from '@/composables/useApiFirebase'
import {
  createEmptyFailReason,
  isFailReasonComplete,
  toFailReasonPayload,
} from '@/composables/useDrivingFailReason'
import DrivingFailReasonPicker from '@/components/orders/DrivingFailReasonPicker.vue'
import OrderFamilyCard from '@/components/orders/OrderFamilyCard.vue'
import OrderPartnerChips from '@/components/orders/OrderPartnerChips.vue'
import OutsourcePaymentStatus from '@/components/orders/OutsourcePaymentStatus.vue'
import SplitOrderDialog from '@/components/orders/SplitOrderDialog.vue'
import { orderEventNameHtml } from '@/utils/orderFamily'
import { isFreeOrder } from '@/utils/freeOrder'
import { useDriverAssignment } from '@/composables/useDriverAssignment'
import { isCurrentUserAdmin } from '@/utils/portalRoles'
import {
  OrderDrivingActionTypeEnum,
  OrderStatusEnum,
  OrderStepEnum,
  OrderStepStateEnum,
  ServiceTypeEnum,
} from '@/enums.js'
import type { external } from '@/schema.d'
import { $apiOperi } from '@/utils/apiOperi'
import { skipsAutomaticReceipt } from '@/utils/fiscalReceiptMode'
import { paymentMethodLabel as sharedPaymentMethodLabel } from '@/utils/paymentLabels'
import {
  defaultTenderFor,
  deliveryCompletionBody,
  isMoneySecured,
  isPayOnDeliveryMethod,
  isTenderRequired,
  isUnpaidOverrideNeeded,
  isUnsecuredCardDelivery as isUnsecuredCardDeliveryRule,
} from '@/utils/deliveryTender'
import CustomDatePicker from '@/components/CustomDatePicker.vue'
import OrderPaymentPanel from '@/views/apps/ecommerce/order/OrderPaymentPanel.vue'

// Interface for TimeWindow (needed for resolve dialog logic)
interface TimeWindow {
  startTime?: string
  endTime?: string
}

let cachedNameKeys: Record<string, string> | null = null

const loadNameKeys = async () => {
  if (cachedNameKeys)
    return cachedNameKeys

  const { data } = await useApiFirebase(
    'o/app%2Ftranslations%2Fsr_Latn_RS.json?alt=media',
  )

  cachedNameKeys = (data.value ?? {}) as Record<string, string>

  return cachedNameKeys
}

type OrderResponse =
  | external['components/schemas/response/admin/order/AdminOrderResponse.yaml']
  | undefined

type OrderEventListResponse =
  | external['components/schemas/response/admin/OrderEventListResponse.yaml']
  | undefined

type OrderItem =
  | external['components/schemas/common/order/OrderItem.yaml']
  | undefined

type OrderItems = Array<OrderItem> | undefined

type UserResponse =
  external['components/schemas/response/admin/UserResponse.yaml']
type OrderStepRequest =
  external['components/schemas/common/OrderStepRequest.yaml']

const orderItems = ref<OrderItems>()
const confirmOrderCancel = ref<boolean>(false)
const confirmOrderActivateCanceled = ref<boolean>(false)
const confirmOrderToOnHold = ref<boolean>(false)
const confirmOrderFromOnHold = ref<boolean>(false)
const confirmOutsourceComplete = ref<boolean>(false)
const activeOutsourceTab = ref<string>('approved')

// Add missing reactive variables for Drive functionality
const isDriveDialogOpen = ref(false)
const isDriveResultDialogOpen = ref(false)

const driveActionMessage = ref<{
  type: 'success' | 'error'
  text: string
} | null>(null)

const driveResultType = ref<'success' | 'fail' | null>(null)
const isDriveLoading = ref(false)

const nextDrivingActionInProgress = vueComputed(() => {
  return !!orderData.value?.nextDrivingAction?.inProgress
})

const isOrderCompletedOrCanceled = vueComputed(() => {
  return (
    orderData.value?.orderStatus === OrderStatusEnum.COMPLETED
    || orderData.value?.orderStatus === OrderStatusEnum.CANCELED
  )
})

const route = useRoute('apps-ecommerce-order-details-id')
const isCancelOrderDialogVisible = ref(false)
const isActivateCanceledOrderDialogVisible = ref(false)
const isPutOrderToOnHoldDialogVisible = ref(false)
const isPutOrderFromOnHoldDialogVisible = ref(false)
const isOutsourceCompleteDialogVisible = ref(false)
const isUserInfoEditDialogVisible = ref(false)
const isEditAddressDialogVisible = ref(false)
const isApprovedOrderItemsExpanded = ref(true)
const isOutsourceItemsExpanded = ref(true)
const hasApprovedOrderItems = ref(false)
const hasOutsourceItems = ref(false)

// Money must never be interpolated raw: order rows created before line-level
// rounding hold values like 242.26999999999998, and a double is not a display type.
const formatRsd = (amount?: number | null) => (amount ?? 0).toFixed(2)

const orderItemsColumns = [
  {
    title: 'Service',
    key: 'name',
    type: 'string',
    sortable: true,
    map: (item: any) =>
      keyValueList.get(item.service.nameKey) ?? item.service.nameKey,
  },
  {
    title: 'Price',
    key: 'finalPrice',
    type: 'number',
    sortable: true,
    map: (item: any) => item.service.finalPrice,
    render: (item: any) => `${formatRsd(item.service.finalPrice)} RSD`,
  },
  {
    title: 'Quantity',
    key: 'quantity',
    type: 'number',
    sortable: true,
  },
  {
    title: 'Total',
    key: 'finalTotalPrice',
    type: 'number',
    sortable: true,

    // Always format: historical rows predate line-level rounding and still hold
    // values like 242.26999999999998.
    render: (item: any) => `<b>${formatRsd(item.finalTotalPrice)} RSD</b>`,
  },
]

const outsourceItemsColumns = [
  {
    title: 'Service',
    key: 'name',
    type: 'string',
    sortable: true,
  },
  {
    title: 'Price',
    key: 'servicePrice',
    type: 'number',
    sortable: true,
    render: (item: any) => `${item.servicePrice} RSD`,
  },
  {
    title: 'Quantity',
    key: 'quantity',
    type: 'number',
    sortable: true,
  },
  {
    title: 'Total',
    key: 'totalPrice',
    type: 'number',
    sortable: true,
    render: (item: any) => `<b>${item.totalPrice} RSD</b>`,
  },
]

/**
 * CustomDataTable renders every cell through `v-html`, so a display name coming back from
 * Firebase - which the account holder chooses - has to be escaped before it goes in. Done
 * here rather than in the shared component, which every table in the app depends on
 * rendering markup.
 */
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// The id is the fallback, not a nicety: an audit row outlives the account named in it, so
// once somebody is deleted the id is all that is left of who did the work.
const personLabel = (name?: string | null, id?: string | null) =>
  escapeHtml(name ?? id ?? '')

const orderEventsColumns = [
  {
    title: 'Event name',
    key: 'eventName',
    type: 'string',
    sortable: true,
    render: (item: any) => orderEventNameHtml(item),
  },
  {
    title: 'Driver',
    key: 'driverName',
    type: 'string',
    sortable: true,
    render: (item: any) => personLabel(item.driverName, item.driverId),
  },
  {
    title: 'Changed by',
    key: 'actorName',
    type: 'string',
    sortable: true,
    render: (item: any) => personLabel(item.actorName, item.actorId),
  },
  {
    title: 'Date',
    key: 'createdAt',
    type: 'datetime',
    sortable: true,
  },
]

/**
 * Drivers share this page, for the order behind a driving of theirs.
 *
 * They get the order itself - the backend confines that to orders assigned to them - and
 * the driving controls. Everything that decides what the order costs or where it is in the
 * pipeline stays with the admin, and so do the two reads below: the event log and the
 * customer record are both admin-only endpoints, and asking for them as a driver would 403
 * the whole page before a single button mattered.
 */
// The rest of this page predates i18n and is hardcoded English. New copy still goes
// through it - a driver is not guaranteed to read English, and this is the one message
// they see when something has gone sideways.
const { t } = useI18n()

/**
 * Dispatch: who is driving this order, and on which date and window.
 *
 * Admin-only. A driver works from the action in front of them and does not decide who
 * carries the order or when - the backend refuses both writes for them anyway, so showing
 * the controls would only be offering clicks that fail.
 */
const {
  assignableDrivers,
  loadDrivers,
  assign,
  pendingOrderId: pendingAssignmentOrderId,
  error: assignmentError,
} = useDriverAssignment()

const dispatchError = vueRef<string | null>(null)
const pendingSlotType = vueRef<'PICKUP' | 'DELIVERY' | null>(null)

const isAdmin = isCurrentUserAdmin()

// Untagged pages default to admin-only in the router guard, so a driver following an order
// from their list or their driving would be bounced before the page loaded. The backend is
// what confines them to their own orders; this only decides who may open the screen.
definePage({
  meta: {
    action: 'read',
    subject: 'Orders',
  },
})

const [nameKeysMap, orderFetch, eventsFetch] = await Promise.all([
  loadNameKeys(),
  useApiOperi<OrderResponse>(`admin/order/${route.params.id}`),
  isAdmin
    ? useApiOperi<OrderEventListResponse>(`admin/orders/${route.params.id}/events`)
    : Promise.resolve({ data: vueRef<OrderEventListResponse | null>(null) }),
])

const keyValueList = new Map(Object.entries(nameKeysMap))

const { data: orderData, execute: fetchOrder } = orderFetch
const { data: orderEvents } = eventsFetch

orderItems.value = orderData.value?.approvedPricing?.orderPricing
  ?.orderItems as OrderItems

// Set visibility flags based on data existence
hasApprovedOrderItems.value
  = !!orderData.value?.approvedPricing?.orderPricing?.items?.length
hasOutsourceItems.value = !!(
  orderData.value?.approvedPricing?.outsourceOrderPricing?.items?.length
  || orderData.value?.draftPricing?.outsourceOrderPricing?.items?.length
)

// Set active outsource tab based on data existence
if (orderData.value?.approvedPricing?.outsourceOrderPricing?.items?.length)
  activeOutsourceTab.value = 'approved'
else if (orderData.value?.draftPricing?.outsourceOrderPricing?.items?.length)
  activeOutsourceTab.value = 'draft'

const { data: userData } = isAdmin
  ? await useApiOperi<UserResponse>(`admin/users/${orderData.value?.customer?.id}`)
  : { data: vueRef<UserResponse | null>(null) }

const cancelOrder = async () => {
  await $apiOperi('admin/order/cancel-pickup', {
    method: 'POST',
    body: {
      id: route.params.id,
    },
  })

  fetchOrder()
}

const activateCanceledOrder = async () => {
  await $apiOperi('admin/order/activate-canceled-pickup', {
    method: 'POST',
    body: {
      id: route.params.id,
    },
  })

  fetchOrder()
}

const putOrderToOnHold = async () => {
  const orderStepRequest = ref<OrderStepRequest>({
    step: orderData.value?.orderSteps?.at(
      orderData.value!.currentOrderStepIndex!,
    )!.step,
    state: OrderStepStateEnum.ON_HOLD,
  })

  await $apiOperi(`admin/order/${route.params.id}/step`, {
    method: 'PUT',
    body: orderStepRequest.value,
  })

  fetchOrder()
}

const putOrderFromOnHold = async () => {
  const orderStepRequest = ref<OrderStepRequest>({
    step: orderData.value?.orderSteps?.at(
      orderData.value!.currentOrderStepIndex!,
    )!.step,
    state: OrderStepStateEnum.IN_PROGRESS,
  })

  await $apiOperi(`admin/order/${route.params.id}/step`, {
    method: 'PUT',
    body: orderStepRequest.value,
  })

  fetchOrder()
}

const markOutsourceProcessingComplete = async () => {
  await $apiOperi(`admin/order/${route.params.id}/outsource-complete`, {
    method: 'POST',
  })

  fetchOrder()
}

const currentOrderStep = vueComputed(() =>
  orderData.value?.orderSteps?.at(orderData.value.currentOrderStepIndex!),
)

const canMarkOutsourceComplete = vueComputed(
  () =>
    hasOutsourceItems.value
    && currentOrderStep.value?.step === OrderStepEnum.LAUNDRY_PROCESSING
    && currentOrderStep.value?.state === OrderStepStateEnum.IN_PROGRESS
    && !isOrderCompletedOrCanceled.value,
)

watch(confirmOrderCancel, () => {
  console.log('Cancelling order')
  if (confirmOrderCancel.value === true)
    cancelOrder()
})

watch(confirmOrderActivateCanceled, () => {
  console.log('Activating canceled order')
  if (confirmOrderActivateCanceled.value === true)
    activateCanceledOrder()
})

watch(confirmOrderToOnHold, () => {
  console.log('Order put on hold')
  if (confirmOrderToOnHold.value === true)
    putOrderToOnHold()
})

watch(confirmOrderFromOnHold, () => {
  console.log('Order put from on hold')
  if (confirmOrderFromOnHold.value === true)
    putOrderFromOnHold()
})

watch(confirmOutsourceComplete, () => {
  if (confirmOutsourceComplete.value === true)
    markOutsourceProcessingComplete()
})

// --- RESOLVE DIALOG LOGIC (IDENTICAL TO index.vue, adapted for orderData) ---
const isResolveDialogOpen = vueRef(false)
const pickupDate = vueRef<Date | null>(null)
const deliveryDate = vueRef<Date | null>(null)
const isResolving = vueRef(false)

const resolveMessage = vueRef<{
  type: 'success' | 'error'
  text: string
} | null>(null)

const pickupTimeWindows = vueRef<TimeWindow[]>([])
const deliveryTimeWindows = vueRef<TimeWindow[]>([])
const selectedPickupTimeWindows = vueRef<TimeWindow[]>([])
const selectedDeliveryTimeWindows = vueRef<TimeWindow[]>([])
const modifyPickup = vueRef(false)
const modifyDelivery = vueRef(false)
const nextDrivingActionDueDate = vueRef<Date | null>(null)

const nextDrivingActionType = vueComputed(
  () => orderData.value?.nextDrivingAction?.type || null,
)

const isStoreAction = vueComputed(
  () =>
    nextDrivingActionType.value === OrderDrivingActionTypeEnum.STORE_PICKUP
    || nextDrivingActionType.value === OrderDrivingActionTypeEnum.STORE_DELIVERY,
)

const isCustomerPickup = vueComputed(
  () =>
    nextDrivingActionType.value === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP,
)

const isCustomerDelivery = vueComputed(
  () =>
    nextDrivingActionType.value
    === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY,
)

const canModifyPickup = vueComputed(() => {
  if (isStoreAction.value)
    return false

  return isCustomerPickup.value
})

const canModifyDelivery = vueComputed(() => true)

const fetchAvailableDatesSettings = async () => {
  try {
    const { data: availableDatesSettings } = await useApiOperi<{
      pickupTimeWindows?: TimeWindow[]
      deliveryTimeWindows?: TimeWindow[]
    }>('/available-date-rules/get-active')

    if (availableDatesSettings.value) {
      pickupTimeWindows.value
        = availableDatesSettings.value.pickupTimeWindows || []
      deliveryTimeWindows.value
        = availableDatesSettings.value.deliveryTimeWindows || []
    }
  }
  catch (error) {
    console.error('Failed to fetch available dates settings:', error)
    pickupTimeWindows.value = []
    deliveryTimeWindows.value = []
  }
}

const showResolveDialog = async () => {
  // Always clear date fields on open
  pickupDate.value = null
  deliveryDate.value = null
  nextDrivingActionDueDate.value = null
  resolveMessage.value = null
  isResolveDialogOpen.value = true
  await fetchAvailableDatesSettings()
  selectedPickupTimeWindows.value = [...pickupTimeWindows.value]
  selectedDeliveryTimeWindows.value = [...deliveryTimeWindows.value]
  if (isStoreAction.value) {
    modifyPickup.value = false
    modifyDelivery.value = false
  }
  else if (isCustomerPickup.value) {
    modifyPickup.value = true
    modifyDelivery.value = false
  }
  else if (isCustomerDelivery.value) {
    if (canModifyPickup.value) {
      modifyPickup.value = false
      modifyDelivery.value = false
    }
    else {
      modifyPickup.value = false
      modifyDelivery.value = true
    }
  }
}

const closeResolveDialog = () => {
  resolveMessage.value = null
  isResolveDialogOpen.value = false
  pickupDate.value = null
  deliveryDate.value = null
  selectedPickupTimeWindows.value = []
  selectedDeliveryTimeWindows.value = []
}

const isResolveButtonDisabled = vueComputed(() => {
  if (!orderData.value)
    return true
  if (isResolving.value)
    return true
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  const isValidDate = (date: unknown) => {
    if (!date)
      return false
    const d = new Date(date as any)

    d.setHours(0, 0, 0, 0)

    return d >= today
  }

  if (isStoreAction.value) {
    if (
      !nextDrivingActionDueDate.value
      || !isValidDate(nextDrivingActionDueDate.value)
    )
      return true
    if (canModifyPickup.value && modifyPickup.value) {
      if (
        !pickupDate.value
        || !isValidDate(pickupDate.value)
        || selectedPickupTimeWindows.value.length === 0
      )
        return true
    }

    if (canModifyDelivery.value && modifyDelivery.value) {
      if (
        !deliveryDate.value
        || !isValidDate(deliveryDate.value)
        || selectedDeliveryTimeWindows.value.length === 0
      )
        return true
    }

    if (
      modifyPickup.value
      && modifyDelivery.value
      && pickupDate.value
      && deliveryDate.value
    ) {
      if (new Date(deliveryDate.value) <= new Date(pickupDate.value))
        return true
    }

    return false
  }
  if (isCustomerPickup.value) {
    if (
      !pickupDate.value
      || !isValidDate(pickupDate.value)
      || selectedPickupTimeWindows.value.length === 0
    )
      return true
    if (canModifyDelivery.value && modifyDelivery.value) {
      if (
        !deliveryDate.value
        || !isValidDate(deliveryDate.value)
        || selectedDeliveryTimeWindows.value.length === 0
      )
        return true
      if (
        pickupDate.value
        && deliveryDate.value
        && new Date(deliveryDate.value) <= new Date(pickupDate.value)
      )
        return true
    }

    return false
  }
  if (isCustomerDelivery.value) {
    if (
      !deliveryDate.value
      || !isValidDate(deliveryDate.value)
      || selectedDeliveryTimeWindows.value.length === 0
    )
      return true
    if (canModifyPickup.value && modifyPickup.value) {
      if (
        !pickupDate.value
        || !isValidDate(pickupDate.value)
        || selectedPickupTimeWindows.value.length === 0
      )
        return true
      if (
        pickupDate.value
        && deliveryDate.value
        && new Date(deliveryDate.value) <= new Date(pickupDate.value)
      )
        return true
    }

    return false
  }

  return true
})

const minDateForPicker = vueComputed(() => {
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  return today
})

const formatTimeWindow = (window: TimeWindow): string => {
  if (!window.startTime || !window.endTime)
    return ''
  const start = window.startTime.substring(0, 5)
  const end = window.endTime.substring(0, 5)

  return `${start} - ${end}`
}

const toggleTimeWindow = (window: TimeWindow, isPickup: boolean) => {
  const targetArray = isPickup
    ? selectedPickupTimeWindows
    : selectedDeliveryTimeWindows

  const existingIndex = targetArray.value.findIndex(
    w => w.startTime === window.startTime && w.endTime === window.endTime,
  )

  if (existingIndex >= 0)
    targetArray.value.splice(existingIndex, 1)
  else targetArray.value.push({ ...window })
}

const isTimeWindowSelected = (
  window: TimeWindow,
  isPickup: boolean,
): boolean => {
  const targetArray = isPickup
    ? selectedPickupTimeWindows
    : selectedDeliveryTimeWindows

  return targetArray.value.some(
    w => w.startTime === window.startTime && w.endTime === window.endTime,
  )
}

const minDeliveryDate = vueComputed(() => {
  if (!pickupDate.value)
    return null
  const minDate = new Date(pickupDate.value)

  minDate.setDate(minDate.getDate() + 1)

  return minDate
})

const minPickupDate = vueComputed(() => {
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  return today
})

const resolveOrder = async () => {
  if (!orderData.value) {
    resolveMessage.value = {
      type: 'error',
      text: 'Error: No order data available. Please try again.',
    }

    return
  }
  if (isResolveButtonDisabled.value)
    return

  const orderId = orderData.value.id

  isResolving.value = true
  resolveMessage.value = null
  try {
    const type = orderData.value.nextDrivingAction?.type
    const requestData: any = { orderId }
    if (isStoreAction.value) {
      requestData.nextDrivingActionDate = nextDrivingActionDueDate.value
        ? moment(nextDrivingActionDueDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows = null
      requestData.newPickupDate = null
      requestData.pickupTimeWindows = null
      requestData.newDeliveryDate = null
      requestData.deliveryTimeWindows = null
      if (canModifyPickup.value && modifyPickup.value) {
        requestData.newPickupDate = pickupDate.value
          ? moment(pickupDate.value).format('YYYY-MM-DD')
          : null
        requestData.pickupTimeWindows
          = selectedPickupTimeWindows.value.length > 0
            ? selectedPickupTimeWindows.value
            : null
      }
      if (canModifyDelivery.value && modifyDelivery.value) {
        requestData.newDeliveryDate = deliveryDate.value
          ? moment(deliveryDate.value).format('YYYY-MM-DD')
          : null
        requestData.deliveryTimeWindows
          = selectedDeliveryTimeWindows.value.length > 0
            ? selectedDeliveryTimeWindows.value
            : null
      }
    }
    else if (isCustomerPickup.value) {
      requestData.nextDrivingActionDate = pickupDate.value
        ? moment(pickupDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows
        = selectedPickupTimeWindows.value.length > 0
          ? selectedPickupTimeWindows.value
          : null
      requestData.newPickupDate = pickupDate.value
        ? moment(pickupDate.value).format('YYYY-MM-DD')
        : null
      requestData.pickupTimeWindows
        = selectedPickupTimeWindows.value.length > 0
          ? selectedPickupTimeWindows.value
          : null
      if (canModifyDelivery.value && modifyDelivery.value) {
        requestData.newDeliveryDate = deliveryDate.value
          ? moment(deliveryDate.value).format('YYYY-MM-DD')
          : null
        requestData.deliveryTimeWindows
          = selectedDeliveryTimeWindows.value.length > 0
            ? selectedDeliveryTimeWindows.value
            : null
      }
      else {
        requestData.newDeliveryDate = null
        requestData.deliveryTimeWindows = null
      }
    }
    else if (isCustomerDelivery.value) {
      requestData.nextDrivingActionDate = deliveryDate.value
        ? moment(deliveryDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows
        = selectedDeliveryTimeWindows.value.length > 0
          ? selectedDeliveryTimeWindows.value
          : null
      requestData.newDeliveryDate = deliveryDate.value
        ? moment(deliveryDate.value).format('YYYY-MM-DD')
        : null
      requestData.deliveryTimeWindows
        = selectedDeliveryTimeWindows.value.length > 0
          ? selectedDeliveryTimeWindows.value
          : null
      if (canModifyPickup.value && modifyPickup.value) {
        requestData.newPickupDate = pickupDate.value
          ? moment(pickupDate.value).format('YYYY-MM-DD')
          : null
        requestData.pickupTimeWindows
          = selectedPickupTimeWindows.value.length > 0
            ? selectedPickupTimeWindows.value
            : null
      }
      else {
        requestData.newPickupDate = null
        requestData.pickupTimeWindows = null
      }
    }
    await $apiOperi('admin/order/resolve-on-hold', {
      method: 'PUT',
      body: requestData,
    })
    closeResolveDialog()
    window.location.reload()
  }
  catch (error) {
    resolveMessage.value = {
      type: 'error',
      text: `Failed to resolve order: ${error instanceof Error ? error.message : 'Unknown error'}`,
    }
  }
  finally {
    isResolving.value = false
  }
}

const router = useRouter()

/**
 * Refetches the order, and leaves the page if that is no longer allowed.
 *
 * Finishing a leg can hand the order back: assignment clears the moment the goods leave
 * the driver's hands, at the store drop and again at the customer's door. The refetch that
 * follows is then a request for an order that is not theirs any more, and it comes back
 * 403 - which rendered as a page of blanks, because every binding here is optional-chained
 * off an order that never arrived. An admin keeps access throughout and stays put.
 */
const refreshOrLeave = async () => {
  await fetchOrder()

  if (orderData.value?.id)
    return true

  await router.replace('/apps/ecommerce/order/list')

  return false
}

onMounted(() => {
  if (isAdmin)
    loadDrivers()
})

const onAssignDriver = async (driverId: unknown) => {
  if (await assign(Number(route.params.id), (driverId as string | null) ?? null))
    await fetchOrder()
}

/** The rule's windows for one side, so a plan can only name a slot that exists. */
const ruleWindows = vueRef<{ pickup: TimeWindow[]; delivery: TimeWindow[] }>({
  pickup: [],
  delivery: [],
})

const loadRuleWindows = async () => {
  if (!isAdmin || ruleWindows.value.pickup.length > 0)
    return

  const rule = await $apiOperi<{
    pickupTimeWindows?: TimeWindow[]
    deliveryTimeWindows?: TimeWindow[]
  }>('available-date-rules/get-active')

  ruleWindows.value = {
    pickup: rule.pickupTimeWindows ?? [],
    delivery: rule.deliveryTimeWindows ?? [],
  }
}

onMounted(loadRuleWindows)

const slotWindowLabel = (window?: TimeWindow | null) =>
  window
    ? `${(window.startTime ?? '').slice(0, 5)}–${(window.endTime ?? '').slice(0, 5)}`
    : ''

const windowItemsFor = (slotType: 'PICKUP' | 'DELIVERY') =>
  (slotType === 'PICKUP' ? ruleWindows.value.pickup : ruleWindows.value.delivery)
    .map(window => ({
      title: slotWindowLabel(window),
      value: slotWindowLabel(window),
    }))

/**
 * Writes the date and window this order is actually driven in.
 *
 * The same endpoint the availability planner uses, so an order planned from either screen
 * lands in the same two columns. Clearing the window clears the date with it - a scheduled
 * date with no window is a half-plan nothing downstream knows how to read, which is why
 * the backend refuses to keep one.
 */
/**
 * The date a dispatch window belongs to: the one somebody planned, or the customer's
 * preference until then. Read-only here - moving an order to another day is a decision
 * about that day, taken where the day is visible.
 */
const dispatchDateLabel = (date?: string | null) =>
  date ? moment(date).format('ddd, D MMM') : t('orders.dispatch.notPlanned')

const scheduleSlot = async (
  slotType: 'PICKUP' | 'DELIVERY',
  date: string | null,
  windowLabelValue: unknown,
) => {
  const windows = slotType === 'PICKUP' ? ruleWindows.value.pickup : ruleWindows.value.delivery

  const window = windowLabelValue
    ? windows.find(candidate => slotWindowLabel(candidate) === windowLabelValue) ?? null
    : null

  pendingSlotType.value = slotType
  dispatchError.value = null
  try {
    await $apiOperi(`admin/planned-actions/${route.params.id}/window`, {
      method: 'PUT',
      body: { slotType, date, timeWindow: window },
    })
    await fetchOrder()
  }
  catch (error) {
    dispatchError.value
      = (error as { data?: { message?: string } })?.data?.message
      ?? 'Could not save the plan'
  }
  finally {
    pendingSlotType.value = null
  }
}

// Add a helper function to format time windows for display
function formatTimeWindowDisplay(window: {
  startTime?: string
  endTime?: string
}) {
  if (!window?.startTime || !window?.endTime)
    return ''

  return `${window.startTime.substring(0, 5)} - ${window.endTime.substring(0, 5)}`
}

// Add a mapping for step labels
const orderStepLabels: Record<string, string> = {
  CREATED: 'Order was placed',
  PICKUP: 'Pick-up',
  STORE_DELIVERY: 'Store Delivery',
  LAUNDRY_CLASSIFICATION: 'Laundry Classification',
  LAUNDRY_PROCESSING: 'Laundry Processing',
  STORE_PICKUP: 'Store Pickup',
  DELIVERY: 'Delivery',
}

// Add a helper to format address for next driving action
function formatDrivingActionAddress(address: any): string {
  if (!address)
    return ''

  const addressParts = [
    address.streetName || address.address || '',
    address.streetNumber || '',
    address.city || '',
    address.postalCode || address.zip || '',
  ].filter(Boolean)

  return addressParts.join(', ')
}
function getMapsLink(address: any): string {
  if (
    address
    && address.geoLocation
    && typeof address.geoLocation.lat === 'number'
    && typeof address.geoLocation.lng === 'number'
  )
    return `https://www.google.com/maps/place/${address.geoLocation.lat},${address.geoLocation.lng}`

  return ''
}

// Define the 7 canonical steps in order
const canonicalSteps = [
  'CREATED',
  'PICKUP',
  'STORE_DELIVERY',
  'LAUNDRY_CLASSIFICATION',
  'LAUNDRY_PROCESSING',
  'STORE_PICKUP',
  'DELIVERY',
]

// Add Drive action handlers
const handleDrive = async () => {
  if (!orderData.value?.nextDrivingAction)
    return

  isDriveLoading.value = true
  driveActionMessage.value = null

  try {
    const type = orderData.value.nextDrivingAction.type

    const isCustomer
      = type === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
      || type === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY

    const isPickup
      = type === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
      || type === OrderDrivingActionTypeEnum.STORE_PICKUP

    const endpoint = isCustomer
      ? 'admin/order-customer-driving-instructions'
      : 'admin/order-store-driving-instructions'

    const payload = {
      ordersToPickup: isPickup ? [route.params.id] : [],
      ordersToDeliver: !isPickup ? [route.params.id] : [],
    }

    await $apiOperi(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })

    await fetchOrder()
    isDriveDialogOpen.value = false
    driveActionMessage.value = {
      type: 'success',
      text: 'Drive action started successfully',
    }
  }
  catch (error) {
    // Prefer the backend's own explanation (ofetch exposes the response body on
    // .data); error.message is just "[PUT] ...: 409 Conflict".
    const reason
      = (error as { data?: { message?: string } })?.data?.message
      ?? (error instanceof Error ? error.message : 'Unknown error')

    driveActionMessage.value = {
      type: 'error',
      text: `Failed to start drive action: ${reason}`,
    }
  }
  finally {
    isDriveLoading.value = false
  }
}

const paymentMethodLabel = computed(() =>
  sharedPaymentMethodLabel(orderData.value?.paymentMethod),
)

// --- completing a customer delivery: money is recorded with it ---

// What came in at the door, or nothing. The real tenders are the money, NOT_COLLECTED is
// the branch that keeps the unpaid-delivery override, and is the only option a pay-on-delivery
// order never gets - there is nothing else for it to be completed as.
const deliveryTender = ref<'CASH' | 'CARD_TERMINAL' | 'IPS' | 'NOT_COLLECTED'>('CASH')
const overrideUnpaid = ref(false)
const overrideReason = ref('')

const isPayOnDeliveryOrder = computed(() =>
  isPayOnDeliveryMethod(orderData.value?.paymentMethod),
)

// Priced at 0 with no money taken: nothing to secure, ask about or collect.
const isFree = computed(() =>
  isFreeOrder(
    orderData.value?.approvedPricing?.orderPricing?.finalTotalPrice,
    orderData.value?.payment?.paymentStatus,
  ),
)

// A card order about to be driven to the customer with nothing reserved and nothing
// charged. The backend lets the dispatch through - stopping it would strand the
// laundry rather than secure the money - so the warning is the operator's only cue.
const isUnsecuredCustomerDelivery = computed(
  () =>
    orderData.value?.nextDrivingAction?.type
      === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY
    && !isPayOnDeliveryOrder.value
    && !isFree.value
    && !isMoneySecured(orderData.value?.payment?.paymentStatus),
)

const isCustomerDeliverySuccess = computed(
  () =>
    orderData.value?.nextDrivingAction?.type
      === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY
    && driveResultType.value === 'success',
)

// A card delivery whose money is not secured: the reservation failed, or none was ever taken.
// The customer usually just pays at the door, so this is offered the same two tenders a
// pay-on-delivery order gets - plus the option of saying nothing was collected.
const isUnsecuredCardDelivery = computed(
  () =>
    isCustomerDeliverySuccess.value
    && !isFree.value
    && isUnsecuredCardDeliveryRule(
      orderData.value?.paymentMethod,
      orderData.value?.payment?.paymentStatus,
    ),
)

const showDeliveryTenderPicker = computed(
  () =>
    isCustomerDeliverySuccess.value
    && !isFree.value
    && isTenderRequired(
      orderData.value?.paymentMethod,
      orderData.value?.payment?.paymentStatus,
    ),
)

// Only the branch where the laundry really leaves unpaid still owes a written reason. Money
// taken at the door answers the same question, and the backend accepts the tender in place of
// the override.
/**
 * A written reason is owed only when the laundry actually leaves unpaid - which can only
 * happen at the customer's door.
 *
 * `isUnpaidOverrideNeeded` answers from the payment alone, because that is all it knows.
 * Without the leg check the warning, the checkbox and the required reason appeared on a
 * customer pickup and on both store hand-overs of any unsecured card order, and
 * `canSubmitDriveResult` below then refused the result until they were filled in - asking a
 * driver collecting laundry to justify an unpaid delivery that has not happened and, on that
 * leg, cannot.
 */
const needsUnpaidOverride = computed(() =>
  isCustomerDeliverySuccess.value
  && !isFree.value
  && isUnpaidOverrideNeeded(
    orderData.value?.paymentMethod,
    orderData.value?.payment?.paymentStatus,
    deliveryTender.value,
  ),
)

// A partner's cash is left for an admin to fiscalize, so the caption must not promise a receipt.
const deliveryReceiptCaption = computed(() =>
  skipsAutomaticReceipt(orderData.value?.fiscalReceiptMode, deliveryTender.value)
    ? t('partner.fiscal.noReceiptOnCash')
    : 'This issues a real fiscal receipt and cannot be undone.',
)

const failReason = ref(createEmptyFailReason())

const isCustomerDrivingAction = computed(() => {
  const type = orderData.value?.nextDrivingAction?.type

  return (
    type === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
    || type === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY
  )
})

const canSubmitDriveResult = computed(
  () =>
    (!needsUnpaidOverride.value
      || (overrideUnpaid.value && overrideReason.value.trim().length > 0))

    // A customer failure with no reason cannot be attributed, and an unattributed
    // failed pickup is one we can neither charge for nor justify not charging for.
    // Store and storage failures are nobody's fault to charge, so they need none.
    && (driveResultType.value !== 'fail'
      || !isCustomerDrivingAction.value
      || isFailReasonComplete(failReason.value)),
)

// Reset every answer whenever the dialog is reopened, so a previous action's reason, tender or
// override cannot be submitted for a different one - each of these arms a real, irreversible
// consequence (a fiscal receipt, an unpaid-delivery alert).
watch(isDriveResultDialogOpen, isOpen => {
  if (!isOpen)
    return

  failReason.value = createEmptyFailReason()
  overrideUnpaid.value = false
  overrideReason.value = ''

  // Default to whatever the customer chose at checkout; staff change it when reality differed
  // (dead terminal, no cash on hand). A card order defaults to NOT_COLLECTED instead: opening
  // this dialog must never quietly arm a fiscal receipt for money nobody took.
  deliveryTender.value = defaultTenderFor(orderData.value?.paymentMethod)
})

const handleDriveResult = async () => {
  if (!orderData.value?.nextDrivingAction || !driveResultType.value)
    return

  isDriveLoading.value = true
  driveActionMessage.value = null

  try {
    const type = orderData.value.nextDrivingAction.type

    const isCustomer
      = type === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
      || type === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY

    const isPickup
      = type === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
      || type === OrderDrivingActionTypeEnum.STORE_PICKUP

    // A successful customer DELIVERY goes through the single-order endpoint,
    // which records the money in the same step: a pay-on-delivery order cannot
    // be completed without saying what was collected, and an unpaid card order
    // is refused unless explicitly overridden. Everything else keeps using the
    // bulk driving-result endpoint unchanged.
    if (isCustomerDeliverySuccess.value) {
      await $apiOperi(`admin/order/${route.params.id}/delivery-complete`, {
        method: 'POST',
        body: deliveryCompletionBody(
          isFree.value ? 'NOT_COLLECTED' : deliveryTender.value,
          overrideUnpaid.value,
          overrideReason.value,
        ),
      })

      isDriveResultDialogOpen.value = false
      if (!await refreshOrLeave())
        return

      driveActionMessage.value = {
        type: 'success',
        text: 'Delivery completed',
      }

      return
    }

    const endpoint = isCustomer
      ? 'admin/order-customer-driving-result'
      : 'admin/order-store-driving-result'

    const payload = {
      pickupSuccessOrders:
        isPickup && driveResultType.value === 'success'
          ? [route.params.id]
          : [],
      pickupFailedOrders:
        isPickup && driveResultType.value === 'fail' ? [route.params.id] : [],
      deliverySuccessOrders:
        !isPickup && driveResultType.value === 'success'
          ? [route.params.id]
          : [],
      deliveryFailedOrders:
        !isPickup && driveResultType.value === 'fail' ? [route.params.id] : [],

      // Customer legs only: the backend requires a reason for every failed customer
      // order, and the store endpoint has no such field.
      failReasons:
        isCustomer && driveResultType.value === 'fail'
          ? [toFailReasonPayload(Number(route.params.id), failReason.value)]
          : undefined,
    }

    await $apiOperi(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })

    isDriveResultDialogOpen.value = false
    if (!await refreshOrLeave())
      return

    driveActionMessage.value = {
      type: 'success',
      text: `Drive result ${driveResultType.value} set successfully`,
    }
  }
  catch (error) {
    driveActionMessage.value = {
      type: 'error',
      text: `Failed to set drive result: ${error instanceof Error ? error.message : 'Unknown error'}`,
    }
  }
  finally {
    isDriveLoading.value = false
  }
}
</script>

<template>
  <div>
    <!--
      Said plainly rather than rendered as a page of empty fields. Every binding below is
      optional-chained off the order, so without this an order the caller may not read -
      a stale link, or one handed back a moment ago - looks like a loading page that never
      finishes.
    -->
    <VCard v-if="!orderData?.id">
      <VCardText class="text-center py-12">
        <VIcon
          icon="tabler-lock"
          size="40"
          class="mb-3 text-disabled"
        />
        <div class="text-body-1 mb-4">
          {{ t("orders.details.noAccess") }}
        </div>
        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-arrow-left"
          @click="router.push('/apps/ecommerce/order/list')"
        >
          {{ t("orders.details.noAccessBack") }}
        </VBtn>
      </VCardText>
    </VCard>

    <div
      v-else
      class="d-flex justify-space-between align-center flex-wrap gap-y-4 mb-6"
    >
      <div>
        <div class="d-flex align-center mb-2 flex-wrap">
          <VBtn
            size="small"
            variant="tonal"
            color="primary"
            class="mb-2 mr-2"
            prepend-icon="tabler-arrow-left"
            @click="router.push('/apps/ecommerce/order/list')"
          >
            Back to Orders
          </VBtn>
        </div>
        <div class="d-flex gap-2 align-center mb-2 flex-wrap">
          <h4 class="text-h4">
            Order #{{ route.params.id }}
          </h4>
          <!-- The identifier the customer sees in the app. -->
          <VChip
            v-if="orderData?.code"
            variant="tonal"
            color="secondary"
            label
          >
            #{{ orderData.code }}
          </VChip>
          <div class="d-flex gap-x-2">
            <VChip
              v-if="orderData?.orderStatus === OrderStatusEnum.ACTIVE"
              variant="tonal"
              color="info"
              label
            >
              ACTIVE
            </VChip>
            <VChip
              v-if="orderData?.orderStatus === OrderStatusEnum.COMPLETED"
              variant="tonal"
              color="success"
              label
            >
              COMPLETED
            </VChip>
            <VChip
              v-if="orderData?.orderStatus === OrderStatusEnum.CANCELED"
              variant="tonal"
              color="error"
              label
            >
              CANCELED
            </VChip>
            <VChip
              v-if="orderData?.orderStatus === OrderStatusEnum.ON_HOLD"
              variant="tonal"
              color="error"
              label
            >
              ON HOLD
            </VChip>

            <!-- Quick/Regular Order indicator -->
            <VChip
              :color="
                orderData?.isQuickDeliveryPreferred ? '#d97706' : '#1a53ff'
              "
              :icon="
                orderData?.isQuickDeliveryPreferred
                  ? 'tabler-bolt'
                  : 'tabler-truck'
              "
              variant="tonal"
              label
            >
              {{ orderData?.isQuickDeliveryPreferred ? "QUICK" : "REGULAR" }}
            </VChip>
            <OrderPartnerChips :order="orderData" />
            <VChip
              v-if="isFree"
              variant="tonal"
              color="success"
              prepend-icon="tabler-gift"
              label
            >
              {{ t("freeOrder.chip") }}
            </VChip>
          </div>
        </div>
        <div
          v-if="orderData?.outsourceOrderId"
          class="mb-2"
        >
          <VChip
            prepend-icon="tabler-building-store"
            variant="tonal"
            color="secondary"
            size="large"
            label
          >
            Store ID: {{ orderData.outsourceOrderId }}
          </VChip>
        </div>
        <div>
          <span class="text-body-1">
            {{
              moment(String(orderData?.createdAt)).format("DD/MM/YYYY HH:mm")
            }}
          </span>
        </div>
      </div>

      <!--
        Every control in this bar decides what the order costs or where it sits in the
        pipeline - items, outsource sign-off, on-hold, rescheduling, cancelling. All of it
        is the admin's, so the bar is gated as a whole rather than button by button, which
        is how one gets missed when a seventh is added. The driving controls a driver does
        have live in the Next Driving Action card below.
      -->
      <div
        v-if="isAdmin"
        class="d-flex flex-wrap gap-x-3 gap-y-2 justify-end"
      >
        <VBtn
          v-if="
            (hasApprovedOrderItems || hasOutsourceItems)
              && !isOrderCompletedOrCanceled
          "
          color="primary"
          size="small"
          @click="
            router.push(`/apps/ecommerce/order/approval/${route.params.id}`)
          "
        >
          Manage Order Items
        </VBtn>
        <SplitOrderDialog
          :order="orderData"
          @split="() => fetchOrder()"
        />
        <VBtn
          v-if="canMarkOutsourceComplete"
          color="success"
          size="small"
          variant="elevated"
          prepend-icon="tabler-circle-check"
          @click="
            isOutsourceCompleteDialogVisible = !isOutsourceCompleteDialogVisible
          "
        >
          Mark Outsource Complete
        </VBtn>
        <VBtn
          v-if="
            orderData?.orderStatus !== OrderStatusEnum.CANCELED
              && orderData?.orderStatus !== OrderStatusEnum.ON_HOLD
              && !isOrderCompletedOrCanceled
          "
          variant="tonal"
          color="primary"
          size="small"
          @click="
            isPutOrderToOnHoldDialogVisible = !isPutOrderToOnHoldDialogVisible
          "
        >
          On Hold
        </VBtn>

        <VBtn
          v-if="
            (orderData?.orderStatus === OrderStatusEnum.ACTIVE
              || orderData?.orderStatus === OrderStatusEnum.ON_HOLD)
              && !isOrderCompletedOrCanceled
          "
          variant="tonal"
          color="success"
          size="small"
          prepend-icon="tabler-calendar-event"
          @click="showResolveDialog"
        >
          {{
            orderData?.orderStatus === OrderStatusEnum.ON_HOLD
              ? "Resolve and Reschedule"
              : "Reschedule"
          }}
        </VBtn>

        <VBtn
          v-if="
            orderData?.orderStatus !== OrderStatusEnum.CANCELED
              && !isOrderCompletedOrCanceled
          "
          variant="tonal"
          color="error"
          size="small"
          @click="isCancelOrderDialogVisible = !isCancelOrderDialogVisible"
        >
          Cancel Order
        </VBtn>

        <VBtn
          v-if="orderData?.orderStatus === OrderStatusEnum.CANCELED"
          variant="tonal"
          color="primary"
          size="small"
          @click="
            isActivateCanceledOrderDialogVisible
              = !isActivateCanceledOrderDialogVisible
          "
        >
          Activate Canceled Order
        </VBtn>
      </div>
    </div>

    <!--
      Dispatch, and admin-only. Who carries the order and when it is driven are planning
      decisions; a driver works the action in front of them. The backend refuses both
      writes for a non-admin regardless - this only keeps the controls off their screen.
    -->
    <VRow v-if="isAdmin && orderData && !isOrderCompletedOrCanceled">
      <VCol>
        <VCard class="mb-6">
          <VCardItem>
            <template #title>
              <h5 class="text-h5">
                {{ t("orders.dispatch.title") }}
              </h5>
            </template>
          </VCardItem>
          <VDivider />
          <VCardText>
            <VAlert
              v-if="dispatchError || assignmentError"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-4"
              closable
            >
              {{ dispatchError || assignmentError }}
            </VAlert>

            <VRow>
              <VCol
                cols="12"
                md="4"
              >
                <!--
                  One assignment for the whole order. It clears itself when the goods change
                  hands, so an order mid-pipeline showing nobody is the normal state.
                -->
                <AppSelect
                  :model-value="orderData.assignedDriverId ?? null"
                  :items="assignableDrivers.map((d) => ({ title: d.fullName, value: d.userId }))"
                  :label="t('orders.dispatch.driverLabel')"
                  :placeholder="t('orders.plan.unassigned')"
                  :loading="pendingAssignmentOrderId === orderData.id"
                  clearable
                  @update:model-value="onAssignDriver"
                />
              </VCol>

              <VCol
                cols="12"
                md="4"
              >
                <AppSelect
                  :model-value="
                    slotWindowLabel(
                      orderData.pickupDatesAndSlotsInfo?.scheduledPickupTimeWindow,
                    ) || null
                  "
                  :items="windowItemsFor('PICKUP')"
                  :label="t('orders.dispatch.pickupWindow')"
                  :placeholder="t('orders.dispatch.notPlanned')"
                  :loading="pendingSlotType === 'PICKUP'"
                  clearable
                  @update:model-value="
                    (value) =>
                      scheduleSlot(
                        'PICKUP',
                        orderData?.pickupDatesAndSlotsInfo?.scheduledPickupDate
                          ?? orderData?.pickupDatesAndSlotsInfo?.preferredPickupDate
                          ?? null,
                        value,
                      )
                  "
                />
                <!--
                  The date the window belongs to, stated rather than editable. Which day an
                  order is driven is a decision about the day - it is taken on the
                  availability planner, where the rest of that day is visible - so setting it
                  from one order's page was a way to move an order somewhere nobody was
                  looking.
                -->
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ t("orders.dispatch.appliesTo") }}
                  {{
                    dispatchDateLabel(
                      orderData.pickupDatesAndSlotsInfo?.scheduledPickupDate
                        ?? orderData.pickupDatesAndSlotsInfo?.preferredPickupDate,
                    )
                  }}
                </div>
              </VCol>

              <VCol
                cols="12"
                md="4"
              >
                <AppSelect
                  :model-value="
                    slotWindowLabel(
                      orderData.deliveryDatesAndSlotsInfo?.scheduledDeliveryTimeWindow,
                    ) || null
                  "
                  :items="windowItemsFor('DELIVERY')"
                  :label="t('orders.dispatch.deliveryWindow')"
                  :placeholder="t('orders.dispatch.notPlanned')"
                  :loading="pendingSlotType === 'DELIVERY'"
                  clearable
                  @update:model-value="
                    (value) =>
                      scheduleSlot(
                        'DELIVERY',
                        orderData?.deliveryDatesAndSlotsInfo?.scheduledDeliveryDate
                          ?? orderData?.deliveryDatesAndSlotsInfo?.preferredDeliveryDate
                          ?? null,
                        value,
                      )
                  "
                />
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ t("orders.dispatch.appliesTo") }}
                  {{
                    dispatchDateLabel(
                      orderData.deliveryDatesAndSlotsInfo?.scheduledDeliveryDate
                        ?? orderData.deliveryDatesAndSlotsInfo?.preferredDeliveryDate,
                    )
                  }}
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Read-only for a driver: who is carrying it, without the controls. -->
    <VAlert
      v-else-if="orderData?.assignedDriverName"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-6"
    >
      {{ t("orders.dispatch.assignedTo", { name: orderData.assignedDriverName }) }}
    </VAlert>

    <VRow>
      <VCol>
        <OrderFamilyCard
          v-if="orderData"
          :order="orderData"
        />
        <!-- New Card: Next Driving Action Information -->
        <VCard
          v-if="orderData?.nextDrivingAction && !isOrderCompletedOrCanceled"
          class="mb-6"
        >
          <VCardItem>
            <template #title>
              <h5 class="text-h5">
                Next Driving Action
              </h5>
            </template>
          </VCardItem>
          <VDivider />
          <VCardText>
            <VAlert
              v-if="
                orderData.nextDrivingAction.type === 'STORE_PICKUP'
                  && orderData.orderSteps[orderData.currentOrderStepIndex]?.step
                    !== 'STORE_PICKUP'
              "
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              Outsource is still in progress
            </VAlert>
            <VRow>
              <VCol
                cols="12"
                sm="4"
              >
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  Action Type
                </div>
                <VChip
                  :color="
                    orderData.nextDrivingAction.type.includes('CUSTOMER')
                      ? orderData.nextDrivingAction.type === 'CUSTOMER_PICKUP'
                        ? '#1a53ff'
                        : '#0033cc'
                      : orderData.nextDrivingAction.type === 'STORE_PICKUP'
                        ? '#d97706'
                        : '#b45309'
                  "
                  :icon="
                    orderData.nextDrivingAction.type === 'CUSTOMER_PICKUP'
                      ? 'tabler-user-up'
                      : orderData.nextDrivingAction.type === 'CUSTOMER_DELIVERY'
                        ? 'tabler-user-down'
                        : orderData.nextDrivingAction.type === 'STORE_PICKUP'
                          ? 'tabler-building-store'
                          : 'tabler-truck-delivery'
                  "
                  variant="elevated"
                  size="large"
                  class="my-2"
                >
                  {{ orderData.nextDrivingAction.type.replace("_", " ") }}
                </VChip>
              </VCol>

              <VCol
                cols="12"
                sm="4"
              >
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  Due Date
                </div>
                <div class="text-subtitle-2">
                  {{
                    orderData.nextDrivingAction.dueDate
                      ? moment(orderData.nextDrivingAction.dueDate).format(
                        "DD MMM YYYY",
                      )
                      : "Not set"
                  }}
                </div>
              </VCol>

              <VCol
                cols="12"
                sm="4"
              >
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  Due In
                </div>
                <div v-if="orderData.nextDrivingAction.dueDate">
                  <div
                    v-if="
                      moment(orderData.nextDrivingAction.dueDate).diff(
                        moment().startOf('day'),
                        'days',
                      ) < 0
                    "
                    class="text-error font-weight-bold"
                  >
                    <span style="font-size: 1.2em; margin-right: 2px">-</span>{{
                      Math.abs(
                        moment(orderData.nextDrivingAction.dueDate).diff(
                          moment().startOf("day"),
                          "days",
                        ),
                      )
                    }}
                    days ●
                  </div>
                  <div
                    v-else-if="
                      moment(orderData.nextDrivingAction.dueDate).diff(
                        moment().startOf('day'),
                        'days',
                      ) <= 1
                    "
                    class="text-warning font-weight-bold"
                  >
                    {{
                      moment(orderData.nextDrivingAction.dueDate).diff(
                        moment().startOf("day"),
                        "days",
                      )
                    }}
                    days ▲
                  </div>
                  <div
                    v-else
                    class="text-body-1 font-weight-bold"
                  >
                    {{
                      moment(orderData.nextDrivingAction.dueDate).diff(
                        moment().startOf("day"),
                        "days",
                      )
                    }}
                    days
                  </div>
                </div>
                <div
                  v-else
                  class="text-caption text-medium-emphasis"
                >
                  No due date set
                </div>
              </VCol>

              <VCol
                v-if="orderData.nextDrivingAction.preferredTimeWindows?.length"
                cols="12"
              >
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  Preferred Time Windows
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <VChip
                    v-for="(window, index) in orderData.nextDrivingAction
                      .preferredTimeWindows"
                    :key="index"
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    {{ window.startTime?.substring(0, 5) }} -
                    {{ window.endTime?.substring(0, 5) }}
                  </VChip>
                </div>
              </VCol>

              <VCol cols="12">
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  Driving In Progress
                </div>
                <div class="text-body-2">
                  <VChip
                    :color="nextDrivingActionInProgress ? 'success' : 'grey'"
                    size="small"
                  >
                    {{ nextDrivingActionInProgress ? "Yes" : "No" }}
                  </VChip>
                </div>
              </VCol>

              <VCol
                v-if="orderData.nextDrivingAction.address"
                cols="12"
              >
                <div class="d-flex align-center gap-2 mt-2">
                  <span class="font-weight-medium">Address:</span>
                  <span>{{
                    formatDrivingActionAddress(
                      orderData.nextDrivingAction.address,
                    )
                  }}</span>
                  <a
                    v-if="getMapsLink(orderData.nextDrivingAction.address)"
                    :href="getMapsLink(orderData.nextDrivingAction.address)"
                    target="_blank"
                    class="ms-2"
                  >
                    <VIcon
                      icon="tabler-map-pin"
                      size="20"
                      color="primary"
                    />
                  </a>
                </div>
              </VCol>
              <VCol
                v-if="
                  orderData.nextDrivingAction.contactName
                    || orderData.nextDrivingAction.contactPhone
                "
                cols="12"
              >
                <div class="d-flex align-center gap-4 mt-2">
                  <div
                    v-if="orderData.nextDrivingAction.contactName"
                    class="text-body-1 font-weight-bold"
                    style="font-size: 1.1em"
                  >
                    <VIcon
                      icon="tabler-user"
                      size="18"
                      class="me-1"
                    />{{
                      orderData.nextDrivingAction.contactName
                    }}
                  </div>
                  <div
                    v-if="orderData.nextDrivingAction.contactPhone"
                    class="text-body-1 font-weight-bold"
                    style="font-size: 1.1em"
                  >
                    <VIcon
                      icon="tabler-phone"
                      size="18"
                      class="me-1"
                    />{{
                      orderData.nextDrivingAction.contactPhone
                    }}
                  </div>
                </div>
              </VCol>

              <VCol cols="12">
                <div class="d-flex gap-2 mt-2">
                  <!-- Hide all action buttons if order is ON_HOLD -->
                  <template v-if="orderData?.orderStatus !== OrderStatusEnum.ON_HOLD">
                    <VBtn
                      v-if="!nextDrivingActionInProgress"
                      color="primary"
                      variant="elevated"
                      prepend-icon="tabler-car"
                      size="large"
                      @click="
                        () => {
                          driveActionMessage = null;
                          isDriveDialogOpen = true;
                        }
                      "
                    >
                      Drive
                    </VBtn>
                    <VBtn
                      v-if="nextDrivingActionInProgress"
                      color="success"
                      variant="elevated"
                      prepend-icon="tabler-check"
                      size="large"
                      @click="
                        () => {
                          driveActionMessage = null;
                          driveResultType = 'success';
                          isDriveResultDialogOpen = true;
                        }
                      "
                    >
                      Drive Result Success
                    </VBtn>
                    <VBtn
                      v-if="nextDrivingActionInProgress"
                      color="error"
                      variant="elevated"
                      prepend-icon="tabler-x"
                      size="large"
                      @click="
                        () => {
                          driveActionMessage = null;
                          driveResultType = 'fail';
                          isDriveResultDialogOpen = true;
                        }
                      "
                    >
                      Drive Result Fail
                    </VBtn>
                  </template>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- Approved Pricing Section -->
        <VCard
          v-if="hasApprovedOrderItems"
          class="mb-6"
        >
          <VCardItem>
            <template #title>
              <div class="d-flex justify-space-between align-center w-100">
                <div class="d-flex align-center">
                  <h5 class="text-h5">
                    Approved Order Items
                  </h5>
                  <VBtn
                    variant="text"
                    density="compact"
                    icon="tabler-chevron-down"
                    :class="{ 'rotate-180': !isApprovedOrderItemsExpanded }"
                    @click="
                      isApprovedOrderItemsExpanded
                        = !isApprovedOrderItemsExpanded
                    "
                  />
                </div>
                <div>
                  <b>{{
                    formatRsd(
                      orderData?.approvedPricing?.orderPricing
                        ?.finalTotalPrice,
                    )
                  }}
                    RSD</b>
                </div>
              </div>
            </template>
          </VCardItem>
          <VExpandTransition>
            <div v-show="isApprovedOrderItemsExpanded">
              <VDivider />
              <VCardText>
                <h6 class="text-h6 mb-2">
                  Laundry Services
                </h6>
              </VCardText>
              <CustomDataTable
                :items="
                  orderData?.approvedPricing?.orderPricing?.items?.filter(
                    (item: any) =>
                      item.service.type
                      === ServiceTypeEnum.STANDARD_LAUNDRY_SERVICE
                      || item.service.type
                        === ServiceTypeEnum.SPECIAL_LAUNDRY_SERVICE,
                  ) || []
                "
                :columns="orderItemsColumns"
                :initial-sort-by="{ key: 'service.nameKey', order: 'asc' }"
                :show-filters="false"
                :show-pagination="false"
              />
              <VDivider />

              <VCardText>
                <h6 class="text-h6 mb-2">
                  Additional Services
                </h6>
              </VCardText>
              <CustomDataTable
                :items="
                  orderData?.approvedPricing?.orderPricing?.items?.filter(
                    (item: any) =>
                      item.service.type
                      !== ServiceTypeEnum.STANDARD_LAUNDRY_SERVICE
                      && item.service.type
                        !== ServiceTypeEnum.SPECIAL_LAUNDRY_SERVICE,
                  ) || []
                "
                :columns="orderItemsColumns"
                :initial-sort-by="{ key: 'service.nameKey', order: 'asc' }"
                :show-filters="false"
                :show-pagination="false"
              />
              <VDivider />

              <VCardText
                v-if="
                  orderData?.approvedPricing?.orderPricing?.fixedDiscount > 0
                "
              >
                <div class="d-flex justify-space-between align-center px-4">
                  <div>
                    <h6 class="text-h6 mb-2">
                      {{
                        orderData?.approvedPricing?.orderPricing
                          ?.discountPercent
                          ? `Discount (${orderData.approvedPricing.orderPricing.discountPercent}%)`
                          : "Discount"
                      }}
                    </h6>
                    <span class="text-sm text-medium-emphasis">
                      Spread across the item totals above (already reflected in
                      each line total and the order total)
                    </span>
                  </div>
                  <div>
                    <b>
                      {{
                        orderData?.approvedPricing?.orderPricing?.fixedDiscount
                      }}
                      RSD</b>
                  </div>
                </div>
              </VCardText>
            </div>
          </VExpandTransition>
        </VCard>

        <!-- Outsource Items Section -->
        <VCard
          v-if="hasOutsourceItems"
          class="mb-6"
        >
          <VCardItem>
            <template #title>
              <div class="d-flex justify-space-between align-center w-100">
                <div class="d-flex align-center">
                  <h5 class="text-h5">
                    Outsource Items
                  </h5>
                  <VBtn
                    variant="text"
                    density="compact"
                    icon="tabler-chevron-down"
                    :class="{ 'rotate-180': !isOutsourceItemsExpanded }"
                    @click="
                      isOutsourceItemsExpanded = !isOutsourceItemsExpanded
                    "
                  />
                </div>
                <div class="d-flex align-center gap-4">
                  <div>
                    <span
                      v-if="
                        orderData?.approvedPricing?.outsourceOrderPricing
                          && activeOutsourceTab === 'approved'
                      "
                      class="text-sm text-medium-emphasis"
                    >
                      {{
                        moment(
                          String(
                            orderData?.approvedPricing?.outsourceOrderPricing
                              ?.receivedAt,
                          ),
                        ).format("DD/MM/YYYY HH:mm")
                      }}
                    </span>
                    <span
                      v-else-if="
                        orderData?.draftPricing?.outsourceOrderPricing
                          && activeOutsourceTab === 'draft'
                      "
                      class="text-sm text-medium-emphasis"
                    >
                      {{
                        moment(
                          String(
                            orderData?.draftPricing?.outsourceOrderPricing
                              ?.receivedAt,
                          ),
                        ).format("DD/MM/YYYY HH:mm")
                      }}
                    </span>
                  </div>
                </div>
                <div class="d-flex align-center gap-4">
                  <div>
                    <b
                      v-if="
                        orderData?.approvedPricing?.outsourceOrderPricing
                          && activeOutsourceTab === 'approved'
                      "
                    >
                      {{
                        orderData?.approvedPricing?.outsourceOrderPricing
                          ?.totalPrice
                      }}
                      RSD
                    </b>
                    <b
                      v-else-if="
                        orderData?.draftPricing?.outsourceOrderPricing
                          && activeOutsourceTab === 'draft'
                      "
                    >
                      {{
                        orderData?.draftPricing?.outsourceOrderPricing
                          ?.totalPrice
                      }}
                      RSD
                    </b>
                  </div>
                </div>
              </div>
            </template>
          </VCardItem>

          <VCardText
            v-if="isAdmin && orderData?.approvedPricing?.outsourceOrderPricing?.items?.length"
            class="pt-0"
          >
            <OutsourcePaymentStatus
              :order="orderData"
              @refresh="fetchOrder"
            />
          </VCardText>

          <div
            v-if="
              orderData?.approvedPricing?.outsourceOrderPricing
                || orderData?.draftPricing?.outsourceOrderPricing
            "
          >
            <VTabs v-model="activeOutsourceTab">
              <VTab
                v-if="orderData?.approvedPricing?.outsourceOrderPricing"
                value="approved"
              >
                Approved
              </VTab>
              <VTab
                v-if="orderData?.draftPricing?.outsourceOrderPricing"
                value="draft"
              >
                Draft
              </VTab>
            </VTabs>

            <VExpandTransition>
              <VWindow
                v-show="isOutsourceItemsExpanded"
                v-model="activeOutsourceTab"
              >
                <VWindowItem
                  v-if="orderData?.approvedPricing?.outsourceOrderPricing"
                  value="approved"
                >
                  <VDivider />
                  <CustomDataTable
                    :items="
                      orderData?.approvedPricing?.outsourceOrderPricing
                        ?.items || []
                    "
                    :columns="outsourceItemsColumns"
                    :initial-sort-by="{ key: 'name', order: 'asc' }"
                    :show-filters="false"
                    :show-pagination="false"
                  />
                </VWindowItem>

                <VWindowItem
                  v-if="orderData?.draftPricing?.outsourceOrderPricing"
                  value="draft"
                >
                  <VDivider />
                  <CustomDataTable
                    :items="
                      orderData?.draftPricing?.outsourceOrderPricing?.items
                        || []
                    "
                    :columns="outsourceItemsColumns"
                    :initial-sort-by="{ key: 'name', order: 'asc' }"
                    :show-filters="false"
                    :show-pagination="false"
                  />
                </VWindowItem>
              </VWindow>
            </VExpandTransition>
          </div>
        </VCard>

        <VCard
          class="mb-6"
          title="Laundry"
        >
          <VCardText class="d-flex flex-column gap-y-4">
            <div v-if="orderData?.standardLaundryPickupWeight">
              <span class="font-weight-bold">Standard laundry pickup weight:</span>
              <span class="ms-2">{{ orderData?.standardLaundryPickupWeight }} kg</span>
            </div>
            <div v-if="orderData?.laundrySoftener">
              <span class="font-weight-bold">Softener:</span>
              <span class="ms-2">{{ orderData?.laundrySoftener }}</span>
            </div>
            <div v-if="orderData?.laundryPacking">
              <span class="font-weight-bold">Special items packing:</span>
              <span class="ms-2">{{ orderData.laundryPacking }}</span>
            </div>
          </VCardText>
        </VCard>

        <VCard
          v-if="orderData?.customerComment"
          class="mb-6"
          title="Customer comment"
        >
          <VCardText class="d-flex flex-column gap-y-6">
            {{ orderData?.customerComment }}
          </VCardText>
        </VCard>

        <VCard
          v-if="orderData?.workerComment"
          class="mb-6"
          title="Worker comment"
        >
          <VCardText class="d-flex flex-column gap-y-6">
            {{ orderData?.workerComment }}
          </VCardText>
        </VCard>

        <!-- 👉 Shipping Activity -->
        <VCard
          title="Shipping Activity"
          class="mb-6"
        >
          <VCardText>
            <VTimeline
              truncate-line="both"
              side="end"
              line-color="secondary"
              density="compact"
              class="v-timeline-density-compact"
            >
              <!-- 👉 Created order timeline -->
              <VTimelineItem
                v-for="stepName in canonicalSteps"
                :key="stepName"
                :dot-color="
                  (() => {
                    const step = orderData?.orderSteps?.find(
                      (s: any) => s.step === stepName,
                    );
                    if (!step || !step.state) return '#ffffff';
                    if (step.state === 'COMPLETED') return 'success';
                    if (step.state === 'IN_PROGRESS') return 'primary';
                    if (step.state === 'PENDING') return 'warning';
                    return '#ffffff';
                  })()
                "
                :variant="
                  (() => {
                    const step = orderData?.orderSteps?.find(
                      (s: any) => s.step === stepName,
                    );
                    return !step || !step.state ? 'outlined' : undefined;
                  })()
                "
                size="x-small"
              >
                <div class="d-flex justify-space-between align-center">
                  <span class="app-timeline-title">{{ stepName }}</span>
                  <span
                    v-if="
                      orderData?.orderSteps?.find(
                        (s: any) => s.step === stepName,
                      )?.completedDateTime
                    "
                    class="app-timeline-meta"
                  >
                    {{
                      moment(
                        String(
                          orderData?.orderSteps?.find(
                            (s: any) => s.step === stepName,
                          )?.completedDateTime,
                        ),
                      ).format("DD MMM YYYY")
                    }}
                    {{
                      moment(
                        String(
                          orderData?.orderSteps?.find(
                            (s: any) => s.step === stepName,
                          )?.completedDateTime,
                        ),
                      ).format("HH:mm")
                    }}
                  </span>
                </div>
                <p class="app-timeline-text mb-0">
                  <template
                    v-if="
                      orderData?.orderSteps?.find(
                        (s: any) => s.step === stepName,
                      )?.state === 'PENDING'
                    "
                  >
                    Pending
                  </template>
                  <template
                    v-else-if="
                      orderData?.orderSteps?.find(
                        (s: any) => s.step === stepName,
                      )?.state === 'IN_PROGRESS'
                    "
                  >
                    In progress
                  </template>
                  <template
                    v-else-if="
                      orderData?.orderSteps?.find(
                        (s: any) => s.step === stepName,
                      )?.state === 'COMPLETED'
                    "
                  >
                    Completed
                  </template>
                  <template v-else>
                    Not started
                  </template>
                </p>
              </VTimelineItem>
            </VTimeline>
            <!-- 👉 End Created order timeline -->
          </VCardText>
        </VCard>
        <!-- 👉 Order Events -->
        <!--
          Fed by an admin-only endpoint the driver never calls, so for them it would be an
          empty card rather than a shorter page.
        -->
        <VCard
          v-if="isAdmin"
          class="mb-6"
        >
          <VCardItem>
            <template #title>
              <h5 class="text-h5">
                Order Events
              </h5>
            </template>
          </VCardItem>

          <VDivider />
          <CustomDataTable
            :items="orderEvents?.orderEventList || []"
            :columns="orderEventsColumns"
            :initial-sort-by="{ key: 'createdAt', order: 'desc' }"
          />
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="4"
      >
        <!-- 👉 Customer Details  -->
        <!--
          The customer's whole record, from an admin-only endpoint. A driver needs the name,
          address and phone for the stop in front of them, and those arrive with the driving
          action itself - not with the account behind it.
        -->
        <VCard
          v-if="isAdmin"
          class="mb-6"
        >
          <VCardText class="d-flex flex-column gap-y-6">
            <div class="text-body-1 text-high-emphasis font-weight-medium">
              Customer Details
            </div>

            <div class="d-flex align-center">
              <VAvatar
                :image="userData?.userDetails?.photoUrl"
                class="me-3"
              />
              <div>
                <RouterLink
                  :to="`/apps/ecommerce/customer/details/${orderData?.customer?.id}`"
                  class="text-body-1 font-weight-medium text-primary text-decoration-none"
                >
                  {{ userData?.userDetails?.name || userData?.userDetails?.email || orderData?.customer?.email }}
                </RouterLink>
                <div class="text-sm text-disabled">
                  Customer ID: {{ userData?.userDetails?.id }}
                </div>
              </div>
            </div>

            <div>
              <VAvatar
                variant="tonal"
                color="success"
                class="me-3"
              >
                <VIcon icon="tabler-shopping-cart" />
              </VAvatar>
              <span class="text-body-1 font-weight-medium text-high-emphasis">{{ userData?.orders?.length }} Orders</span>
            </div>

            <div class="d-flex flex-column gap-y-1">
              <div class="d-flex justify-space-between align-center text-body-2">
                <span class="text-body-1 text-high-emphasis font-weight-medium">Contact Info</span>
              </div>
              <span>Email: {{ userData?.userDetails?.email }}</span>
              <span>Mobile: {{ userData?.userDetails?.phone }}</span>
            </div>
          </VCardText>
        </VCard>

        <!-- 👉 Pickup Address -->
        <VCard class="mb-6">
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div class="text-body-1 text-high-emphasis font-weight-medium">
                Pickup Address
              </div>
            </div>
            <VList class="card-list mt-2">
              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Street name:</span>
                  <span>
                    {{ orderData?.pickupAddress?.streetName }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Street number:</span>
                  <span>
                    {{ orderData?.pickupAddress?.streetNumber }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Floor:</span>
                  <span>
                    {{ orderData?.pickupAddress?.floor }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Suite number:</span>
                  <span>
                    {{ orderData?.pickupAddress?.suiteNumber }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Instructions:</span>
                  <span>
                    {{ orderData?.pickupAddress?.instructions }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Preferred pickup date:</span>
                  <span>
                    {{
                      orderData?.pickupDatesAndSlotsInfo?.preferredPickupDate
                        != null
                        ? moment(
                          String(
                            orderData?.pickupDatesAndSlotsInfo
                              ?.preferredPickupDate,
                          ),
                        ).format("DD MMM YYYY")
                        : ""
                    }}
                  </span>
                </div>
              </VListItem>
              <VListItem
                v-if="
                  orderData?.pickupDatesAndSlotsInfo?.preferredPickupTimeWindows
                    ?.length
                "
              >
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Preferred pickup time windows:</span>
                  <span>
                    <span
                      v-for="(window, idx) in orderData.pickupDatesAndSlotsInfo
                        .preferredPickupTimeWindows"
                      :key="idx"
                    >
                      {{ formatTimeWindowDisplay(window)
                      }}<span
                        v-if="
                          idx
                            < orderData.pickupDatesAndSlotsInfo
                              .preferredPickupTimeWindows.length
                            - 1
                        "
                      >,
                      </span>
                    </span>
                  </span>
                </div>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>

        <!-- 👉 Delivery Address -->
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div class="text-body-1 text-high-emphasis font-weight-medium">
                Delivery Address
              </div>
            </div>
            <VList class="card-list mt-2">
              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Street name:</span>
                  <span>
                    {{ orderData?.deliveryAddress?.streetName }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Street number:</span>
                  <span>
                    {{ orderData?.deliveryAddress?.streetNumber }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Floor:</span>
                  <span>
                    {{ orderData?.deliveryAddress?.floor }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Suite number:</span>
                  <span>
                    {{ orderData?.deliveryAddress?.suiteNumber }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Instructions:</span>
                  <span>
                    {{ orderData?.deliveryAddress?.instructions }}
                  </span>
                </div>
              </VListItem>

              <VListItem>
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Preferred delivery date:</span>
                  <span>
                    {{
                      orderData?.deliveryDatesAndSlotsInfo
                        ?.preferredDeliveryDate != null
                        ? moment(
                          String(
                            orderData?.deliveryDatesAndSlotsInfo
                              ?.preferredDeliveryDate,
                          ),
                        ).format("DD MMM YYYY")
                        : ""
                    }}
                  </span>
                </div>
              </VListItem>
              <VListItem
                v-if="
                  orderData?.deliveryDatesAndSlotsInfo
                    ?.preferredDeliveryTimeWindows?.length
                "
              >
                <div class="text-body-1">
                  <span class="font-weight-medium me-2">Preferred delivery time windows:</span>
                  <span>
                    <span
                      v-for="(window, idx) in orderData
                        .deliveryDatesAndSlotsInfo.preferredDeliveryTimeWindows"
                      :key="idx"
                    >
                      {{ formatTimeWindowDisplay(window)
                      }}<span
                        v-if="
                          idx
                            < orderData.deliveryDatesAndSlotsInfo
                              .preferredDeliveryTimeWindows.length
                            - 1
                        "
                      >,
                      </span>
                    </span>
                  </span>
                </div>
              </VListItem>
            </VList>

            <div class="mt-6">
              <div class="text-body-1 text-body-1 text-high-emphasis font-weight-medium">
                Payment method
              </div>
              <div class="text-body-1">
                {{ paymentMethodLabel }}
              </div>
            </div>

            <OrderPaymentPanel
              :order="orderData"
              :order-id="route.params.id"
              @refresh="fetchOrder"
            />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <ConfirmDialog
      v-model:is-dialog-visible="isCancelOrderDialogVisible"
      v-model:confirm="confirmOrderCancel"
      confirmation-question="Are you sure?"
      cancel-msg=""
      cancel-title="Order Not Canceled"
      confirm-msg="Your order cancelled successfully."
      confirm-title="Cancelled!"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isActivateCanceledOrderDialogVisible"
      v-model:confirm="confirmOrderActivateCanceled"
      confirmation-question="Are you sure?"
      cancel-msg=""
      cancel-title="Order Not Activated"
      confirm-msg="Your order is activated successfully."
      confirm-title="Activated!"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isPutOrderToOnHoldDialogVisible"
      v-model:confirm="confirmOrderToOnHold"
      confirmation-question="Put on hold?"
      cancel-msg=""
      cancel-title="Order Not Put On Hold"
      confirm-msg="Order set to on hold successfully."
      confirm-title="On Hold!"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isPutOrderFromOnHoldDialogVisible"
      v-model:confirm="confirmOrderFromOnHold"
      confirmation-question="Return from on hold?"
      cancel-msg=""
      cancel-title="Order Not Put On Hold"
      confirm-msg="Order set to on hold successfully."
      confirm-title="On Hold!"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isOutsourceCompleteDialogVisible"
      v-model:confirm="confirmOutsourceComplete"
      confirmation-question="Mark outsource processing as complete?"
      cancel-msg=""
      cancel-title="Outsource Processing Not Marked Complete"
      confirm-msg="Order advanced to next step."
      confirm-title="Outsource Processing Complete!"
    />

    <UserInfoEditDialog v-model:is-dialog-visible="isUserInfoEditDialogVisible" />

    <AddEditAddressDialog v-model:is-dialog-visible="isEditAddressDialogVisible" />

    <!-- Add the Resolve Dialog -->
    <VDialog
      v-model="isResolveDialogOpen"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{
            orderData?.orderStatus === OrderStatusEnum.ON_HOLD
              ? "Resolve and Reschedule Order"
              : "Reschedule Order"
          }}
        </VCardTitle>
        <VCardText v-if="!orderData">
          <VAlert type="error">
            Error: Unable to load order details. Please try again.
          </VAlert>
        </VCardText>
        <VCardText v-else>
          <div class="mb-4">
            <p class="text-h6">
              Updating order <strong>#{{ orderData.id }}</strong>
            </p>
            <p v-if="orderData.customer">
              for {{ orderData.customer.name }}
            </p>
            <p class="text-h5 mt-1 mb-2">
              {{ orderData.nextDrivingAction?.type }}
            </p>
          </div>
          <!-- STORE_PICKUP / STORE_DELIVERY -->
          <VRow v-if="isStoreAction">
            <VCol cols="12">
              <CustomDatePicker
                label="Due Date"
                :model-value="[
                  nextDrivingActionDueDate,
                  nextDrivingActionDueDate,
                ]"
                :range="false"
                @update:model-value="
                  (val) =>
                    (nextDrivingActionDueDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
            </VCol>
            <VCol
              v-if="canModifyPickup"
              cols="12"
            >
              <VCheckbox
                v-model="modifyPickup"
                label="Modify Customer Pickup Time"
              />
            </VCol>
            <VCol
              v-if="modifyPickup"
              cols="12"
            >
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol cols="12">
              <VCheckbox
                v-model="modifyDelivery"
                label="Modify Customer Delivery Time"
              />
            </VCol>
            <VCol
              v-if="modifyDelivery"
              cols="12"
            >
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>
          <!-- CUSTOMER_PICKUP -->
          <VRow v-else-if="isCustomerPickup">
            <VCol cols="12">
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol
              v-if="canModifyDelivery"
              cols="12"
            >
              <VCheckbox
                v-model="modifyDelivery"
                label="Modify Customer Delivery Time"
              />
            </VCol>
            <VCol
              v-if="modifyDelivery"
              cols="12"
            >
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>
          <!-- CUSTOMER_DELIVERY -->
          <VRow v-else-if="isCustomerDelivery">
            <VCol cols="12">
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol
              v-if="canModifyPickup"
              cols="12"
            >
              <VCheckbox
                v-model="modifyPickup"
                label="Modify Customer Pickup Time"
              />
            </VCol>
            <VCol
              v-if="modifyPickup"
              cols="12"
            >
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>
          <VAlert
            v-if="resolveMessage"
            :type="resolveMessage.type"
            class="mt-4"
            closable
          >
            {{ resolveMessage.text }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VBtn
            color="secondary"
            variant="outlined"
            :disabled="isResolving"
            @click="closeResolveDialog"
          >
            Cancel
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            variant="elevated"
            :disabled="isResolveButtonDisabled"
            :loading="isResolving"
            size="large"
            @click="resolveOrder"
          >
            <VIcon
              start
              icon="tabler-calendar-event"
            />
            {{
              orderData?.orderStatus === OrderStatusEnum.ON_HOLD
                ? "Resolve and Reschedule"
                : "Reschedule"
            }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Dialogs for confirmation -->
    <VDialog
      v-model="isDriveDialogOpen"
      max-width="400"
    >
      <VCard>
        <VCardTitle>Start Drive?</VCardTitle>
        <VCardText>
          Are you sure you want to start DRIVE for this order?
        </VCardText>
        <VCardText
          v-if="isUnsecuredCustomerDelivery"
          class="pt-0"
        >
          <VAlert
            type="warning"
            variant="tonal"
            density="compact"
          >
            This order is paid by card online and its money is not secured -
            nothing reserved, nothing charged. You can send the driver anyway;
            sort the payment out in the Payment panel.
          </VAlert>
        </VCardText>
        <VCardActions>
          <VBtn
            variant="text"
            @click="isDriveDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            :loading="isDriveLoading"
            @click="handleDrive"
          >
            Confirm
          </VBtn>
        </VCardActions>
        <VAlert
          v-if="driveActionMessage"
          :type="driveActionMessage.type"
          class="mt-2"
        >
          {{ driveActionMessage.text }}
        </VAlert>
      </VCard>
    </VDialog>
    <VDialog
      v-model="isDriveResultDialogOpen"
      max-width="400"
    >
      <VCard>
        <VCardTitle>Set Drive Result?</VCardTitle>
        <VCardText>
          Are you sure you want to set DRIVE RESULT
          <b>{{ driveResultType }}</b> for this order?

          <!--
            Completing the delivery records the money and issues the fiscal receipt, so the
            tender has to be stated here. A pay-on-delivery order always states one. A card
            order does too when its money is not secured - the reservation failed and the
            customer pays at the door - and only then gets the "nothing was collected" branch,
            which is what the unpaid-delivery override hangs off.
          -->
          <div
            v-if="isCustomerDeliverySuccess && isFree"
            class="text-body-2 mt-4"
          >
            {{ t("freeOrder.nothingToCollect") }}
          </div>
          <template v-if="showDeliveryTenderPicker">
            <div class="text-body-2 font-weight-medium mt-4 mb-1">
              What did the customer pay with?
            </div>
            <VAlert
              v-if="isUnsecuredCardDelivery"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-2"
            >
              This order was placed as card online and its money is not secured.
              Whatever the customer actually handed over is what gets recorded
              and printed on the receipt.
            </VAlert>
            <VRadioGroup
              v-model="deliveryTender"
              hide-details
            >
              <VRadio
                label="Cash"
                value="CASH"
              />
              <VRadio
                label="Card on the terminal"
                value="CARD_TERMINAL"
              />
              <VRadio
                label="IPS QR code"
                value="IPS"
              />
              <VRadio
                v-if="isUnsecuredCardDelivery"
                label="Nothing - the laundry leaves unpaid"
                value="NOT_COLLECTED"
              />
            </VRadioGroup>
            <div
              v-if="deliveryTender !== 'NOT_COLLECTED'"
              class="text-caption text-medium-emphasis mt-1"
            >
              {{ deliveryReceiptCaption }}
            </div>
          </template>

          <!--
            Nothing was collected and the card money is not secured. Recording the delivery
            anyway is allowed, because the laundry is already gone - but not silently.
          -->
          <template v-if="needsUnpaidOverride">
            <VAlert
              type="warning"
              variant="tonal"
              class="mt-4"
            >
              This order is paid by card online and its payment is not secured.
              Completing the delivery means the laundry leaves unpaid.
            </VAlert>
            <VCheckbox
              v-model="overrideUnpaid"
              label="Complete the delivery anyway"
              hide-details
            />
            <VTextField
              v-if="overrideUnpaid"
              v-model="overrideReason"
              label="Why?"
              placeholder="e.g. bags already handed over, card declined at the door"
              class="mt-2"
            />
          </template>

          <DrivingFailReasonPicker
            v-if="driveResultType === 'fail' && isCustomerDrivingAction"
            v-model="failReason"
            class="mt-4"
          />
        </VCardText>
        <VCardActions>
          <VBtn
            variant="text"
            @click="isDriveResultDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VSpacer />
          <VBtn
            :color="
              driveResultType === 'success'
                ? 'success'
                : driveResultType === 'fail'
                  ? 'error'
                  : 'primary'
            "
            :loading="isDriveLoading"
            :disabled="!driveResultType || !canSubmitDriveResult"
            @click="handleDriveResult"
          >
            Confirm
          </VBtn>
        </VCardActions>
        <VAlert
          v-if="driveActionMessage"
          :type="driveActionMessage.type"
          class="mt-2"
        >
          {{ driveActionMessage.text }}
        </VAlert>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.text-error {
  color: rgb(var(--v-theme-error));
}

.text-warning {
  color: rgb(var(--v-theme-warning));
}

.rotate-180 {
  transform: rotate(-180deg);
}
</style>
